/**
 * Renewal dates are calendar dates stored as YYYY-MM-DD strings, so they never
 * shift across time zones and compare correctly as plain strings.
 */

const pad = (n: number) => String(n).padStart(2, '0');

/** Today's date in the browser's time zone, as YYYY-MM-DD. */
export function todayIso(now: Date = new Date()): string {
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/** Adds (or with a negative number, subtracts) whole days to a YYYY-MM-DD date. */
export function addDaysIso(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** The input if it is a YYYY-MM-DD date, otherwise null (date inputs report '' while typing). */
export function isoOrNull(s: string): string | null {
  return /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/.test(s) && !Number.isNaN(Date.parse(`${s}T00:00:00Z`)) ? s : null;
}

export type RenewalStatus = 'overdue' | 'soon' | 'ok';

export function renewalStatus(dateIso: string, today: string, soonDays: number): RenewalStatus {
  if (dateIso < today) return 'overdue';
  if (dateIso <= addDaysIso(today, soonDays)) return 'soon';
  return 'ok';
}

/** Whole days from today to the date; negative when it has passed. */
export function daysUntil(dateIso: string, today: string): number {
  const ms = Date.parse(`${dateIso}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`);
  return Math.round(ms / 86_400_000);
}

const dateFormat = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeZone: 'UTC' });

export function formatIsoDate(iso: string): string {
  return dateFormat.format(new Date(`${iso}T00:00:00Z`));
}

const dateTimeFormat = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' });

export function formatDateTime(d: Date): string {
  return dateTimeFormat.format(d);
}
